import { ChangeDetectorRef, Component, HostListener, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SimpleDropdownOption } from '@components/simple-dropdown/simple-dropdown.component';
import { TranslateService } from '@ngx-translate/core';
import { maxModbusSettings, ModbusFunctionCode, ModbusSetting, Port } from 'icreate/common/models/port';
import { getValidationErrors, mapErrors, ValidationErrorMap } from 'icreate/common/validators';
import { modbusSettingValidator } from 'icreate/common/validators/port/port-validator';
import * as _ from 'lodash';

@Component({
    selector: 'app-modbus-settings-dialog',
    templateUrl: './modbus-settings-dialog.component.html',
    styleUrl: './modbus-settings-dialog.component.css',
    standalone: false,
})
export class ModbusSettingsDialogComponent implements OnInit {
    readonly SLAVE_INPUT = 'slaveId';

    readonly START_ADDRESS_INPUT = 'startAddress';
    readonly DATA_INPUT = 'numberOfData';

    modbusSettings: ModbusSetting[];

    initialSettings: ModbusSetting[];
    validationErrors: ValidationErrorMap = {};

    visibleSettingOptions: ModbusSetting;

    @HostListener('document:click', [])
    clickOut() {
        this.visibleSettingOptions = null;
    }

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            port: Port;
            modbusSettings: ModbusSetting[];
        },
        private dialogRef: MatDialogRef<ModbusSettingsDialogComponent>,
        private translate: TranslateService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.modbusSettings = this.data.modbusSettings ?? [];
        this.initialSettings = _.cloneDeep(this.modbusSettings);
    }

    closeDialog() {
        this.dialogRef.close();
    }

    save() {
        if (this.isSaveDisabled) return;
        this.dialogRef.close(this.modbusSettings);
    }

    openOptions(setting): void {
        this.visibleSettingOptions = setting;
    }

    addUnit() {
        const newModbusSetting: ModbusSetting = new ModbusSetting(
            this.modbusSettings.length + 1,
            this.data.port?.protocol?.id
        );
        this.modbusSettings.push(newModbusSetting);
    }

    handleFunctionCodeChanged({ value }, setting: ModbusSetting) {
        setting.functionCode = value;
    }

    onInputChange({ value, name }, setting: ModbusSetting) {
        if (name == this.SLAVE_INPUT) {
            setting.slaveId = value;
        } else if (name == this.START_ADDRESS_INPUT) {
            setting.startAddress = Number(value);
        } else if (name == this.DATA_INPUT) {
            setting.numberOfData = Number(value);
        }
    }

    deleteSetting(setting: ModbusSetting) {
        this.modbusSettings = this.modbusSettings.filter(set => set.index != setting.index);
    }

    duplicate(setting: ModbusSetting) {
        this.modbusSettings.push({ ...setting, id: null, index: this.modbusSettings.length + 1 });
    }

    get functionCodeOptions() {
        const SUPPORTED_FUNCTION_CODES: any = new Set<ModbusFunctionCode>([
            ModbusFunctionCode['01'],
            ModbusFunctionCode['02'],
            ModbusFunctionCode['03'],
            ModbusFunctionCode['04'],
            ModbusFunctionCode['0F'],
            ModbusFunctionCode['10 (Hex)'],
        ]);

        const options: SimpleDropdownOption[] = Object.entries(ModbusFunctionCode)
            .filter(([, value]) => SUPPORTED_FUNCTION_CODES.has(value))
            .map(([key, value]) => ({ value, string: key }));

        return options;
    }

    get isAddDisabled(): boolean {
        return this.modbusSettings.length >= maxModbusSettings;
    }

    get isSaveDisabled() {
        if (_.isEqual(this.initialSettings, this.modbusSettings)) return true;
        if (this.settingsHaveErrors) return true;
        return false;
    }

    get settingsHaveErrors() {
        let hasErrors = false;
        for (let i = 0; i < this.modbusSettings.length; i++) {
            if (hasErrors) break;
            const setting = this.modbusSettings[i];
            const errors = getValidationErrors(modbusSettingValidator(), setting);
            const mappedErrors = mapErrors(errors);
            if (
                mappedErrors['functionCode'] ||
                mappedErrors['slaveId'] ||
                mappedErrors['startAddress'] ||
                mappedErrors['numberOfData']
            ) {
                hasErrors = true;
                break;
            }
        }
        return hasErrors;
    }

    get validatorFunction() {
        return modbusSettingValidator;
    }
}
